import React, { useState, useEffect } from 'react';
import { X, Settings, Shield, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { initAnalytics } from '@/lib/analytics';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Immer aktiv
    functional: false,
    analytics: false,
    marketing: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    // Prüfe ob der Nutzer bereits eine Wahl getroffen hat
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Zeige Banner nach kurzer Verzögerung
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Lade gespeicherte Präferenzen und initialisiere Tracking falls erlaubt
      const saved = JSON.parse(consent);
      setPreferences(saved);
      if (saved.analytics) initAnalytics();
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
    if (prefs.analytics) initAnalytics();
  };

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const acceptEssential = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(essentialOnly);
    savePreferences(essentialOnly);
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]" />

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
        <div className="max-w-6xl mx-auto bg-background border-2 border-[hsl(var(--accent))]/30 rounded-2xl shadow-2xl overflow-hidden">
          {showSettings ? (
            // Detaillierte Einstellungen
            <div className="p-6 sm:p-8 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-[hsl(var(--accent))]" />
                  <h2 className="text-2xl font-bold">Cookie-Einstellungen</h2>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-secondary rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-muted-foreground mb-6">
                Wir verwenden Cookies und ähnliche Technologien, um Ihnen ein optimales Website-Erlebnis zu bieten. 
                Sie können selbst entscheiden, welche Kategorien Sie zulassen möchten.
              </p>

              <div className="space-y-4">
                {/* Essentielle Cookies */}
                <div className="p-4 bg-secondary/30 rounded-xl border border-white/5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">Essentielle Cookies</h3>
                        <span className="text-xs px-2 py-1 bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))] rounded-full">
                          Erforderlich
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Beispiele: Session-Management, Sicherheit, Cookie-Präferenzen
                      </p>
                    </div>
                    <div className="ml-4">
                      <div className="w-12 h-6 bg-[hsl(var(--accent))] rounded-full flex items-center px-1">
                        <div className="w-4 h-4 bg-white rounded-full ml-auto" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Funktionale Cookies */}
                <div className="p-4 bg-secondary/30 rounded-xl border border-white/5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">Funktionale Cookies</h3>
                      <p className="text-sm text-muted-foreground">
                        Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Beispiele: Bevorzugte Sprache, Designeinstellungen, Formular-Daten
                      </p>
                    </div>
                    <label className="ml-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-secondary peer-checked:bg-[hsl(var(--accent))] rounded-full flex items-center px-1 transition-colors">
                        <div className="w-4 h-4 bg-white rounded-full peer-checked:ml-auto transition-all" />
                      </div>
                    </label>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-secondary/30 rounded-xl border border-white/5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">Analyse & Statistik</h3>
                      <p className="text-sm text-muted-foreground">
                        Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Beispiele: Google Analytics, Besucherstatistiken, Heatmaps
                      </p>
                    </div>
                    <label className="ml-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-secondary peer-checked:bg-[hsl(var(--accent))] rounded-full flex items-center px-1 transition-colors">
                        <div className="w-4 h-4 bg-white rounded-full peer-checked:ml-auto transition-all" />
                      </div>
                    </label>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 bg-secondary/30 rounded-xl border border-white/5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">Marketing & Tracking</h3>
                      <p className="text-sm text-muted-foreground">
                        Diese Cookies werden verwendet, um Ihnen relevante Werbung anzuzeigen.
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Beispiele: Instagram Feed, Social Media Plugins, Werbe-Tracking
                      </p>
                    </div>
                    <label className="ml-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-secondary peer-checked:bg-[hsl(var(--accent))] rounded-full flex items-center px-1 transition-colors">
                        <div className="w-4 h-4 bg-white rounded-full peer-checked:ml-auto transition-all" />
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={saveCustom}
                  className="flex-1 px-6 py-3 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Auswahl bestätigen
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 px-6 py-3 bg-secondary hover:bg-secondary/80 text-white rounded-xl font-medium transition-all"
                >
                  Alle akzeptieren
                </button>
              </div>

              <button
                onClick={() => navigate('/datenschutz')}
                className="w-full mt-4 text-sm text-muted-foreground hover:text-[hsl(var(--accent))] transition-colors"
              >
                Weitere Informationen in unserer Datenschutzerklärung
              </button>
            </div>
          ) : (
            // Kompakte Banner-Ansicht
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4 mb-6">
                <Shield className="w-8 h-8 text-[hsl(var(--accent))] flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold mb-3">
                    Wir respektieren Ihre Privatsphäre
                  </h2>
                  <p className="text-muted-foreground text-sm sm:text-base">
                    Diese Website verwendet Cookies und ähnliche Technologien, um Ihnen das bestmögliche Erlebnis zu bieten. 
                    Einige sind erforderlich, andere optional. Sie können Ihre Einstellungen jederzeit anpassen.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={acceptAll}
                  className="px-6 py-3 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white rounded-xl font-medium transition-all"
                >
                  Alle akzeptieren
                </button>
                <button
                  onClick={acceptEssential}
                  className="px-6 py-3 bg-secondary hover:bg-secondary/80 text-white rounded-xl font-medium transition-all"
                >
                  Nur Essentielle
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-6 py-3 border border-white/10 hover:bg-secondary/50 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                >
                  <Settings className="w-5 h-5" />
                  Einstellungen
                </button>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/datenschutz')}
                  className="text-sm text-muted-foreground hover:text-[hsl(var(--accent))] transition-colors underline"
                >
                  Datenschutzerklärung
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
